import { ROUTE_OAUTH } from '@components/Router/constants'

import { isInternalPath } from '@/utils'
import {
  CHECK_AUTH_PARAM,
  COUPON_CODE_PARAM,
  OAUTH_ACCESS_TOKEN_PARAM,
  OAUTH_STATE_PARAM,
  SERVICE_PARAMS,
  SKIP_WELCOME_PARAM,
  UTM_KEYS,
  UTM_SOURCE_PARAM,
} from './constants'
import type { EntryLocation, EntryStrategy, OauthEntry } from './types'

const toQueryPair = (params: URLSearchParams, key: string): string | null => {
  const value = params.get(key)

  if (!value) {
    return null
  }

  return `${key}=${encodeURIComponent(value)}`
}

const serializeUtmParams = (params: URLSearchParams): string =>
  UTM_KEYS.map((key) => toQueryPair(params, key))
    .filter((pair) => pair !== null)
    .join('&')

const readUtmQuery = (params: URLSearchParams): string | null => {
  if (!params.has(UTM_SOURCE_PARAM)) {
    return null
  }

  return serializeUtmParams(params)
}

export const parseRedirectAfterAuth = (
  stateParam: string | null
): string | null => {
  if (!stateParam) {
    return null
  }

  try {
    const parsed = JSON.parse(atob(stateParam))
    const redirect = parsed?.redirectAfterAuth

    if (!isInternalPath(redirect)) {
      return null
    }

    return redirect
  } catch {
    console.error('Problem with parsing google oauth result')

    return null
  }
}

export const readOauthEntry = (
  pathname: string,
  hash: string
): OauthEntry | null => {
  if (pathname !== ROUTE_OAUTH) {
    return null
  }

  const params = new URLSearchParams(hash.slice(1))
  const accessToken = params.get(OAUTH_ACCESS_TOKEN_PARAM)

  if (!accessToken) {
    return null
  }

  const stateParam = params.get(OAUTH_STATE_PARAM)
  const redirectAfterAuth = parseRedirectAfterAuth(stateParam)

  return {
    accessToken,
    redirectAfterAuth,
  }
}

export const resolveEntryStrategy = (
  location: EntryLocation
): EntryStrategy => {
  const params = new URLSearchParams(location.search)

  const utmQuery = readUtmQuery(params)
  const couponCode = params.get(COUPON_CODE_PARAM)
  const skipWelcome = params.has(SKIP_WELCOME_PARAM)
  const oauth = readOauthEntry(location.pathname, location.hash)
  const checkAuth = params.has(CHECK_AUTH_PARAM)

  return {
    utmQuery,
    couponCode,
    skipWelcome,
    oauth,
    checkAuth,
  }
}

export const removeServiceParams = (search: string): string | null => {
  const params = new URLSearchParams(search)

  const found = SERVICE_PARAMS.some((param) => params.has(param))

  if (!found) {
    return null
  }

  SERVICE_PARAMS.forEach((param) => {
    params.delete(param)
  })

  const query = params.toString()

  return query ? `?${query}` : ''
}
